















import { defineComponent, useRouter, ref, Ref, watch, onMounted } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import isEmpty from 'lodash/fp/isEmpty'

// Store
import { useSearchStore } from '@/store/search'

// Composables
import useSearchUrl from '@/composables/search/useSearchUrl'
import useFacets from '@/composables/search/useFacets'

// Common
import { decodeHTMLEntities } from '@/common/utils/characters'
import { NESTED_FACET_NAMES } from '@/common/static/search'
import { multiSelectFacets, FACETNAMES } from '@/common/static/facets'

// Types
import { SelectedFacetInterface, FacetObjectInterface, QueryStyle } from '@/types/search/searchTypes'

export default defineComponent({
  props: {
    options: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const { getSelectedFacetCount, existingFacetIndex, deleteFacetValue, mapToFacetName } = useFacets()
    const searchStore = useSearchStore()
    const { facets, getResults: results } = storeToRefs(searchStore)
    const { getSearchUrl } = useSearchUrl()
    const router = useRouter()
    const selectedFacets: Ref<Array<SelectedFacetInterface>> = ref([])
    const isMultiSelect = multiSelectFacets.includes(props.options.key)

    const getCheckedFacets = () => {
      // Special treatment for the other facet
      if (props.options.key === NESTED_FACET_NAMES.other.key) {
        const selectedOtherFacets = props.options.facet
          .flatMap((facetObj: FacetObjectInterface) => Object.keys(facetObj))
          .flatMap((nestedFacetKey: string) => {
            if (existingFacetIndex(nestedFacetKey) !== -1) {
              return {
                facetkey: nestedFacetKey,
                facetname: mapToFacetName(nestedFacetKey, FACETNAMES.PRETTY_FACET_NAME),
                count: getSelectedFacetCount(nestedFacetKey, 'true'),
              }
            }
            return null
          })
          .filter((val: string | null) => val !== null)

        selectedFacets.value = isEmpty(selectedOtherFacets) ? null : selectedOtherFacets
      } else {
        selectedFacets.value = checkCheckedFacet(props.options.key)
      }
    }

    const checkCheckedFacet = (key: string) => {
      const facetIndex = existingFacetIndex(key)
      if (facetIndex !== -1) {
        // return an object with the count as well
        return facets.value.checkedFacets[facetIndex][key].map((facetname: string) => {
          return {
            facetname: mapToFacetName(facetname, FACETNAMES.PRETTY_FACET_NAME),
            count: getSelectedFacetCount(props.options.key, facetname),
          }
        })
      } else {
        return null
      }
    }

    const onClickSelectedFacet = (facet: string, selectedFacet: any) => {
      // change facets in store
      if (facet === NESTED_FACET_NAMES.other.key) {
        deleteFacetValue(selectedFacet.facetkey, 'true')
        searchStore.clearMultiselectFacetState()
      } else {
        if (isMultiSelect) {
          // check if this is the last with an count, if so, clear state
          // Or if we click the last one
          if (results.value.facets![props.options.key][0].data?.length === 1 || selectedFacets.value.length === 1) {
            searchStore.clearMultiselectFacetState()
          } else {
            searchStore.setFacetState(props.options.key)
          }
        } else {
          searchStore.clearMultiselectFacetState()
        }

        deleteFacetValue(facet, selectedFacet.facetname)
      }

      searchStore.setQueryStyle(QueryStyle.FILTER_ENGAGED)

      // perform a search
      router.push(getSearchUrl({ noscroll: true }))
    }

    // Init and watch
    watch(results, () => {
      getCheckedFacets()
    })

    onMounted(() => {
      getCheckedFacets()
    })

    return {
      selectedFacets,
      getSelectedFacetCount,
      onClickSelectedFacet,
      decodeHTMLEntities,
    }
  },
})
